import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadialBarChart, RadialBar } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Activity, Users, TrendingUp, RefreshCw, ArrowLeft,
  Trophy, Sparkles, ChevronRight, ChevronLeft, Building2, GraduationCap,
  Ticket, Shield, Search
} from 'lucide-react';
import { exportToExcel, exportToCSV, exportAnalyticsPDF } from '../../utils/exportUtils';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Analytics Sub-components ─────────────────────────────────────────────────
import AnalyticsFilterBar from '../../components/Analytics/AnalyticsFilterBar';
import KPIGrid from '../../components/Analytics/KPIGrid';
import ExportDropdown from '../../components/Analytics/ExportDropdown';
import SupportAnalytics from '../../components/Analytics/SupportAnalytics';
import ActivityStream from '../../components/Analytics/ActivityStream';
import CollegeCards from '../../components/Analytics/CollegeCards';

// ─── Tab config ────────────────────────────────────────────────────────────────
const TAB_CONFIG = [
  { key: 'overview',    label: 'Overview',          icon: Sparkles },
  { key: 'colleges',    label: 'Colleges & Depts',  icon: Building2 },
  { key: 'support',     label: 'Support',           icon: Ticket },
  { key: 'activity',   label: 'Activity & Audit',  icon: Shield },
  { key: 'students',   label: 'Student View',      icon: GraduationCap },
  { key: 'leaderboard',label: 'Leaderboard',       icon: Trophy },
];

const DEFAULT_FILTERS = {
  dateRange: 'year',
  college: '',
  department: '',
  batch: '',
  course: '',
  role: '',
  status: '',
  dateFrom: '',
  dateTo: '',
};

// ─── Date range → API params ──────────────────────────────────────────────────
const getDateParams = (filters) => {
  const { dateRange, dateFrom, dateTo } = filters;
  const now = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  switch (dateRange) {
    case 'today': return { dateFrom: fmt(now), dateTo: fmt(now) };
    case '7days': { const d = new Date(now); d.setDate(d.getDate() - 7); return { dateFrom: fmt(d), dateTo: fmt(now) }; }
    case '30days': { const d = new Date(now); d.setDate(d.getDate() - 30); return { dateFrom: fmt(d), dateTo: fmt(now) }; }
    case 'month': { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { dateFrom: fmt(d), dateTo: fmt(now) }; }
    case 'lastmonth': { const s = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); return { dateFrom: fmt(s), dateTo: fmt(e) }; }
    case 'year': { const d = new Date(now.getFullYear(), 0, 1); return { dateFrom: fmt(d), dateTo: fmt(now) }; }
    case 'custom': return (dateFrom && dateTo) ? { dateFrom, dateTo } : {};
    default: return {};
  }
};

// ─── Helper components ────────────────────────────────────────────────────────
const TabButton = ({ tab, isActive, onClick, isDark }) => (
  <button
    onClick={() => onClick(tab.key)}
    className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 whitespace-nowrap ${
      isActive
        ? 'bg-[#1A3884] text-white shadow-md shadow-[#1A3884]/20'
        : isDark
          ? 'text-gray-400 hover:text-white hover:bg-[#1A3884]/20'
          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`}
  >
    <tab.icon size={15} />
    <span className="hidden md:inline">{tab.label}</span>
  </button>
);

const SectionCard = ({ title, subtitle, children, className = '', delay = 0, isDark }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay }}
    className={`rounded-2xl border p-6 ${isDark ? 'bg-[#002147] border-[#1a3884]/50 shadow-lg' : 'bg-white border-gray-200 shadow-sm'} ${className}`}
  >
    {(title || subtitle) && (
      <div className="mb-5">
        {title && <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>}
        {subtitle && <p className={`text-xs mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{subtitle}</p>}
      </div>
    )}
    {children}
  </motion.div>
);

const FunnelStep = ({ label, value, total, color, index, isDark }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const widthPct = Math.max(20, pct);
  return (
    <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.08 }} className="flex items-center gap-3">
      <div className="w-24 text-right">
        <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{label}</span>
      </div>
      <div className="flex-1 relative">
        <div className={`h-9 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${widthPct}%` }}
            transition={{ duration: 0.8, delay: index * 0.12 }}
            className="h-full rounded-lg flex items-center justify-end pr-3"
            style={{ background: `linear-gradient(90deg, ${color}88, ${color})` }}
          >
            <span className="text-xs font-bold text-white drop-shadow-sm">{value.toLocaleString()}</span>
          </motion.div>
        </div>
      </div>
      <div className="w-10 text-right">
        <span className={`text-xs font-bold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{pct}%</span>
      </div>
    </motion.div>
  );
};

// ─── College Drilldown (shown in Overview when college is selected) ────────────
const CollegeDrilldown = ({ analyticsData, collegeName, isDark }) => {
  const depts = analyticsData?.students?.byDepartment || [];
  const placementScore = useMemo(() => {
    if (!analyticsData) return 0;
    return Math.round(
      (analyticsData.kpis?.courseCompletion || 0) * 0.4 +
      (analyticsData.assessments?.avgScore || 0) * 0.3 +
      (analyticsData.students?.total > 0 ? (analyticsData.students.active / analyticsData.students.total) * 100 : 0) * 0.3
    );
  }, [analyticsData]);

  const maxStudents = depts.length > 0 ? Math.max(...depts.map(d => d.value), 1) : 1;

  const handleExport = () => {
    if (!depts.length) return;
    const rows = depts.map(d => ({
      'College': collegeName,
      'Department': d.name,
      'Students': d.value,
      'College Avg Assessment (%)': Math.round(analyticsData?.assessments?.avgScore || 0),
      'Course Completion (%)': Math.round(analyticsData?.kpis?.courseCompletion || 0),
      'College Placement Score (%)': placementScore,
      'Attendance': 'N/A',
      'Participation': 'N/A',
    }));
    exportToCSV(rows, Object.keys(rows[0]).map(k => ({ header: k, accessor: k })), `${collegeName.replace(/\s+/g, '_')}_Drilldown`);
  };

  return (
    <SectionCard
      title={`${collegeName} — Performance Drilldown`}
      subtitle="College → Departments → Student Metrics. Batch-level breakdown requires enhanced backend endpoint."
      isDark={isDark}
      delay={0.2}
    >
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { l: 'Total Students', v: analyticsData?.students?.total || 0, c: '#3B82F6' },
          { l: 'Active Students', v: analyticsData?.students?.active || 0, c: '#10B981' },
          { l: 'Avg Assessment', v: `${Math.round(analyticsData?.assessments?.avgScore || 0)}%`, c: '#F59E0B' },
          { l: 'Placement Score', v: `${placementScore}%`, c: '#8B5CF6' },
        ].map(item => (
          <div key={item.l} className={`rounded-xl p-4 border text-center ${isDark ? 'border-[#1a3884]/30 bg-[#0F225C]/20' : 'border-gray-200 bg-gray-50'}`}>
            <p className="text-2xl font-black" style={{ color: item.c }}>{item.v}</p>
            <p className={`text-[10px] font-semibold mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.l}</p>
          </div>
        ))}
      </div>

      {/* Dept table */}
      {depts.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className={`text-xs font-bold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Department Breakdown — {depts.length} departments
            </p>
            <button onClick={handleExport} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${isDark ? 'bg-[#1A3884]/30 text-blue-400 hover:bg-[#1A3884]/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
              Export CSV
            </button>
          </div>
          <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-[#1a3884]/30' : 'border-gray-200'}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-[#0F225C]/40' : 'bg-gray-50'}>
                  {['Department', 'Students', 'Assessment', 'Placement', 'Attendance*', 'Participation*'].map(h => (
                    <th key={h} className={`px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-[#1a3884]/20' : 'divide-gray-100'}`}>
                {depts.map(dept => (
                  <tr key={dept.name} className={`transition-colors ${isDark ? 'hover:bg-[#0F225C]/30' : 'hover:bg-gray-50'}`}>
                    <td className={`px-4 py-3 font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>{dept.name}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex items-center gap-2">
                        <div className={`flex-1 h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                          <div className="h-full rounded-full bg-blue-500" style={{ width: `${(dept.value / maxStudents) * 100}%` }} />
                        </div>
                        <span className={`font-bold w-8 text-right ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{dept.value}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-xs font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{Math.round(analyticsData?.assessments?.avgScore || 0)}%</td>
                    <td className={`px-4 py-3 text-xs font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{placementScore}%</td>
                    <td className={`px-4 py-3 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} title="No Attendance model in backend">N/A</td>
                    <td className={`px-4 py-3 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} title="Not tracked">N/A</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={`text-[10px] mt-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            * Requires <code className="font-mono">Attendance</code> model in backend. Batch-level breakdown needs enhanced analytics endpoint.
          </p>
        </>
      ) : (
        <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No department data for this college</p>
        </div>
      )}
    </SectionCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ANALYTICS PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const Analytics = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // ─── UI State ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // ─── Data State ────────────────────────────────────────────────────────────
  const [colleges, setColleges] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Student View local state
  const [studentSearch, setStudentSearch] = useState('');
  const [studentStatusF, setStudentStatusF] = useState('');
  const [studentDeptF, setStudentDeptF] = useState('');
  const [studentCollegeF, setStudentCollegeF] = useState('');
  const [studentPage, setStudentPage] = useState(1);

  // ─── Derived filter lists ──────────────────────────────────────────────────
  const sortedColleges = useMemo(() => {
    return [...colleges].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [colleges]);

  const sortedDepartments = useMemo(() => {
    return [...departmentsList]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map(d => d.name);
  }, [departmentsList]);

  // Keep compatibility for other charts/logic
  const departments = sortedDepartments;

  const batches = useMemo(() => {
    const list = studentsData.map(s => {
      const b = s.batch || s.department?.batch || '';
      return typeof b === 'string' ? b.trim() : '';
    }).filter(Boolean);
    return [...new Set(list)].sort();
  }, [studentsData]);

  // ─── Selected college label ────────────────────────────────────────────────
  const selectedCollege = useMemo(() =>
    colleges.find(c => c._id === filters.college),
    [colleges, filters.college]);

  const collegeLabel = selectedCollege?.collegeName || selectedCollege?.institutionName || 'All Institutes';

  const contextLabel = useMemo(() => {
    let label = 'All institutes — platform-wide data';
    if (filters.college) {
      label = `${collegeLabel}`;
      if (filters.department) label += ` › ${filters.department}`;
      if (filters.batch) label += ` › ${filters.batch}`;
    }
    return label;
  }, [collegeLabel, filters.college, filters.department, filters.batch]);

  // ─── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchColleges = useCallback(async () => {
    try {
      const { data } = await api.get('/colleges', { params: { page: 1, limit: 1000 } });
      if (data.success) {
        setColleges(data.data || []);
        // Lock college_admin to their own college
        const cid = currentUser?.college?._id || currentUser?.college;
        if (cid && currentUser?.role !== 'admin') {
          setFilters(f => ({ ...f, college: cid }));
        }
      }
    } catch (e) { console.error('Colleges fetch error:', e); }
  }, [currentUser]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const endpointId = filters.college || 'all';
      const dateParams = getDateParams(filters);
      const params = { ...dateParams };
      if (filters.department) params.department = filters.department;
      if (filters.batch) params.batch = filters.batch;
      if (filters.status) params.status = filters.status;
      if (filters.role) params.role = filters.role;

      const [analyticsRes, studentsRes] = await Promise.all([
        api.get(`/analytics/college/${endpointId}`, { params }),
        api.get(`/analytics/students/${endpointId}`, { params: { ...params, status: filters.status || undefined } })
          .catch(() => ({ data: { success: true, data: [] } }))
      ]);

      if (analyticsRes.data.success) {
        setAnalyticsData(analyticsRes.data.data);
      }
      if (studentsRes.data.success) setStudentsData(studentsRes.data.data || []);
    } catch (e) {
      console.error('Analytics fetch error:', e);
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/analytics/dashboard');
      if (res.data.success) setDashboardData(res.data.data);
    } catch (e) { console.error('Dashboard fetch error:', e); }
  }, []);

  const fetchUserStats = useCallback(async () => {
    try {
      const res = await api.get('/users/stats');
      if (res.data.success) setUserStats(res.data.data);
    } catch (e) { console.error('User stats fetch error:', e); }
  }, []);

  // ─── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => { fetchColleges(); fetchDashboard(); fetchUserStats(); }, [fetchColleges, fetchDashboard, fetchUserStats]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  useEffect(() => {
    const fetchDepts = async () => {
      const targetCollege = studentCollegeF || filters.college;
      try {
        const params = targetCollege ? { college: targetCollege } : {};
        const { data } = await api.get('/departments', { params });
        if (data.success) {
          setDepartmentsList(data.data || []);
        }
      } catch (e) {
        console.error('Departments fetch error:', e);
      }
    };
    fetchDepts();
  }, [filters.college, studentCollegeF]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleFilterChange = (newFilters) => {
    // If college changes, reset dept/batch
    if (newFilters.college !== filters.college) {
      setFilters({ ...newFilters, department: '', batch: '' });
    } else {
      setFilters(newFilters);
    }
  };

  const handleResetFilters = () => {
    const cid = currentUser?.college?._id || currentUser?.college;
    setFilters({
      ...DEFAULT_FILTERS,
      college: (currentUser?.role !== 'admin' && cid) ? cid : ''
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchAnalytics(), fetchDashboard(), fetchUserStats()]);
    toast.success('Analytics refreshed!');
  };

  // ─── Export ────────────────────────────────────────────────────────────────
  const buildExportData = () => {
    if (!analyticsData) return null;
    return [{
      'College': collegeLabel,
      'Total Students': analyticsData.students?.total || 0,
      'Active Students': analyticsData.students?.active || 0,
      'Total Courses': analyticsData.courses?.total || 0,
      'Avg Assessment Score (%)': Math.round(analyticsData.assessments?.avgScore || 0),
      'Course Completion (%)': Math.round(analyticsData.kpis?.courseCompletion || 0),
    }];
  };

  const exportCols = [
    { header: 'College', accessor: 'College' },
    { header: 'Total Students', accessor: 'Total Students' },
    { header: 'Active Students', accessor: 'Active Students' },
    { header: 'Total Courses', accessor: 'Total Courses' },
    { header: 'Avg Assessment Score (%)', accessor: 'Avg Assessment Score (%)' },
    { header: 'Course Completion (%)', accessor: 'Course Completion (%)' },
  ];

  const handleExportExcel = () => {
    const data = buildExportData();
    if (!data) { toast.error('No data available'); return; }
    exportToExcel(data, exportCols, `Analytics_${collegeLabel}`);
    toast.success('Exported as Excel');
  };
  const handleExportCSV = () => {
    const data = buildExportData();
    if (!data) { toast.error('No data available'); return; }
    exportToCSV(data, exportCols, `Analytics_${collegeLabel}`);
    toast.success('Exported as CSV');
  };
  const handleExportPDF = () => {
    if (!analyticsData) { toast.error('No data available'); return; }
    exportAnalyticsPDF(analyticsData, collegeLabel);
    toast.success('Exported as PDF');
  };

  // ─── Placement Readiness ───────────────────────────────────────────────────
  const placementScore = useMemo(() => {
    if (!analyticsData) return 0;
    const comp = analyticsData.kpis?.courseCompletion || 0;
    const assess = analyticsData.assessments?.avgScore || 0;
    const engage = analyticsData.students?.total > 0
      ? (analyticsData.students.active / analyticsData.students.total) * 100 : 0;
    return Math.round(comp * 0.4 + assess * 0.3 + engage * 0.3);
  }, [analyticsData]);

  const gaugeColor = placementScore >= 70
    ? isDark ? '#34D399' : '#10B981'
    : placementScore >= 50
      ? isDark ? '#FBBF24' : '#F59E0B'
      : isDark ? '#F87171' : '#EF4444';

  const gaugeData = [{ name: 'Readiness', value: placementScore, fill: gaugeColor }];

  // ─── Student Journey Funnel ────────────────────────────────────────────────
  const funnelData = useMemo(() => {
    if (!analyticsData) return [];
    const total = analyticsData.students?.total || 0;
    const active = analyticsData.students?.active || 0;
    const completing = Math.round(total * ((analyticsData.kpis?.courseCompletion || 0) / 100));
    const assessed = analyticsData.assessments?.total ?? 0;
    return [
      { label: 'Enrolled', value: total, color: isDark ? '#3B82F6' : '#284BB3' },
      { label: 'Active', value: active, color: isDark ? '#34D399' : '#10B981' },
      { label: 'Completing', value: completing, color: isDark ? '#FBBF24' : '#F59E0B' },
      { label: 'Assessed', value: assessed, color: isDark ? '#A78BFA' : '#8B5CF6' },
    ];
  }, [analyticsData, isDark]);

  // ─── Student table (filtered) ──────────────────────────────────────────────
  const filteredStudents = useMemo(() => {
    let s = studentsData;
    if (studentSearch.trim()) {
      const q = studentSearch.toLowerCase();
      s = s.filter(st =>
        (st.fullName || '').toLowerCase().includes(q) ||
        (st.email || '').toLowerCase().includes(q) ||
        (st.studentId || '').toLowerCase().includes(q)
      );
    }
    if (studentStatusF) s = s.filter(st => st.status === studentStatusF);
    if (studentDeptF) {
      s = s.filter(st => {
        const dname = typeof st.department === 'object'
          ? (st.department?.abbreviation || st.department?.fullName || st.department?.domain || '')
          : (st.department || '');
        return dname.toLowerCase().includes(studentDeptF.toLowerCase());
      });
    }
    if (studentCollegeF) {
      s = s.filter(st => {
        const cid = st.college?._id || st.college || st.collegeId;
        return cid && cid.toString() === studentCollegeF.toString();
      });
    }
    return s;
  }, [studentsData, studentSearch, studentStatusF, studentDeptF, studentCollegeF]);

  const paginatedStudents = useMemo(() => {
    const start = (studentPage - 1) * 10;
    return filteredStudents.slice(start, start + 10);
  }, [filteredStudents, studentPage]);

  const totalStudentPages = Math.ceil(filteredStudents.length / 10);

  useEffect(() => {
    setStudentPage(1);
  }, [studentSearch, studentStatusF, studentDeptF, studentCollegeF]);

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className={`min-h-screen p-4 lg:p-6 font-sans ${isDark ? 'bg-[#00152E]' : 'bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100'}`}>

      {/* ── PAGE HEADER ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl px-5 py-3 border mb-4 ${isDark ? 'bg-[#002147] border-[#1a3884]/40 shadow-lg' : 'bg-white border-gray-200 shadow-sm'}`}
      >
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2.5 rounded-xl border transition-all ${isDark ? 'border-[#1a3884]/50 hover:bg-[#1a3884]/20 text-gray-300 hover:text-white' : 'border-gray-300 hover:bg-gray-50 text-gray-600 hover:text-gray-900'}`}
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-xl font-bold mb-0.5 ${isDark ? 'text-white' : 'text-[#00152E]'}`}>
                Admin Operations Dashboard
              </h1>
              <div className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <Activity className="w-4 h-4 text-[#284BB3]" />
                <span className="font-medium">{contextLabel}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ExportDropdown
              onExportExcel={handleExportExcel}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isDark={isDark}
            />
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 bg-[#001f40] text-gray-200 hover:bg-[#1a3884]/30' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <RefreshCw className={refreshing ? 'animate-spin' : ''} size={14} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className={`flex items-center gap-1 mt-4 pt-4 border-t overflow-x-auto pb-1 ${isDark ? 'border-[#1a3884]/30' : 'border-gray-200'}`}>
          {TAB_CONFIG.map(tab => (
            <TabButton key={tab.key} tab={tab} isActive={activeTab === tab.key} onClick={setActiveTab} isDark={isDark} />
          ))}
        </div>
      </motion.div>

      {/* ── FILTER BAR (Overview tab only) ──────────────────────────────────── */}
      {activeTab === 'overview' && (
        <AnalyticsFilterBar
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleResetFilters}
          colleges={sortedColleges}
          departments={sortedDepartments}
          batches={batches}
          isDark={isDark}
          user={currentUser}
        />
      )}

      {/* ── LOADING ──────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="relative w-16 h-16">
            <div className={`absolute inset-0 border-4 rounded-full ${isDark ? 'border-[#1a3884]/40' : 'border-gray-200'}`} />
            <div className="absolute inset-0 border-4 border-[#284BB3] rounded-full border-t-transparent animate-spin" />
          </div>
        </div>
      ) : (
        <AnimatePresence mode="wait">

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* OVERVIEW TAB                                                     */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">

              {/* KPI Grid */}
              <KPIGrid
                dashboardData={dashboardData}
                analyticsData={analyticsData}
                userStats={userStats}
                isDark={isDark}
                filters={filters}
              />

              {/* Placement Readiness + Student Journey */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SectionCard title="Placement Readiness" subtitle="Composite score from completion, assessment, and engagement" isDark={isDark} delay={0.1}>
                  <div className="flex flex-col items-center">
                    <div style={{ width: '100%', height: 200 }}>
                      <svg width="0" height="0" />
                      <RadialBarChart
                        width={280}
                        height={180}
                        cx="50%"
                        cy="50%"
                        innerRadius="60%"
                        outerRadius="90%"
                        startAngle={180}
                        endAngle={0}
                        data={gaugeData}
                        style={{ margin: '0 auto' }}
                      >
                        <RadialBar background={{ fill: isDark ? '#1E293B' : '#e2e8f0' }} dataKey="value" cornerRadius={10} />
                      </RadialBarChart>
                    </div>
                    <div className="text-center -mt-16">
                      <p className={`text-4xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{placementScore}%</p>
                      <p className={`text-xs font-semibold mt-1`} style={{ color: gaugeColor }}>
                        {placementScore >= 70 ? 'Strong' : placementScore >= 50 ? 'Moderate' : 'Needs Improvement'}
                      </p>
                      {filters.college && selectedCollege && (
                        <p className={`text-[10px] mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{collegeLabel}</p>
                      )}
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Student Journey" subtitle="Progression from enrollment through assessment" isDark={isDark} delay={0.15}>
                  {funnelData.length > 0 && funnelData[0].value > 0 ? (
                    <div className="space-y-3">
                      {funnelData.map((step, i) => (
                        <FunnelStep key={step.label} {...step} total={funnelData[0].value} index={i} isDark={isDark} />
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium">No student data{filters.college ? ' for selected college' : ''}</p>
                    </div>
                  )}
                </SectionCard>
              </div>

              {/* College Drilldown — visible when a college is selected */}
              {filters.college && analyticsData && (
                <CollegeDrilldown
                  analyticsData={analyticsData}
                  collegeName={collegeLabel}
                  isDark={isDark}
                />
              )}

              {/* No college selected hint */}
              {!filters.college && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border p-6 text-center ${isDark ? 'bg-[#002147] border-[#1a3884]/30' : 'bg-white border-gray-200'}`}
                >
                  <Building2 className={`w-10 h-10 mx-auto mb-3 opacity-40 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <p className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Select a College from the filters to see the department-level drilldown
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Showing platform-wide aggregated data above
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* COLLEGES & DEPARTMENTS TAB                                       */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === 'colleges' && (
            <motion.div key="colleges" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <CollegeCards colleges={colleges} isDark={isDark} />
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* SUPPORT TAB                                                      */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === 'support' && (
            <motion.div key="support" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <SupportAnalytics isDark={isDark} />
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* ACTIVITY & AUDIT TAB                                            */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === 'activity' && (
            <motion.div key="activity" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ActivityStream isDark={isDark} />
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* STUDENT VIEW TAB                                                 */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === 'students' && (
            <motion.div key="students" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">

              {/* Student Filters */}
              <div className={`rounded-2xl border p-5 ${isDark ? 'bg-[#002147] border-[#1a3884]/40' : 'bg-white border-gray-200 shadow-sm'}`}>
                <h3 className={`text-sm font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Student Filters</h3>
                <div className="flex flex-wrap gap-3">
                  {/* Search */}
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border flex-1 min-w-[180px] ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60' : 'bg-gray-50 border-gray-300'}`}>
                    <Search size={13} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                    <input
                      type="text"
                      value={studentSearch}
                      onChange={e => setStudentSearch(e.target.value)}
                      placeholder="Search name, email, ID…"
                      className={`flex-1 text-xs bg-transparent focus:outline-none ${isDark ? 'text-gray-200 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
                    />
                  </div>
                  {/* Status */}
                  <select
                    value={studentStatusF}
                    onChange={e => setStudentStatusF(e.target.value)}
                    className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="graduated">Graduated</option>
                  </select>
                  {/* College */}
                  {currentUser?.role !== 'college_admin' && (
                    <select
                      value={studentCollegeF}
                      onChange={e => setStudentCollegeF(e.target.value)}
                      className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                    >
                      <option value="">All Colleges</option>
                      {sortedColleges.map(c => (
                        <option key={c._id} value={c._id}>
                          {c.collegeName || c.institutionName}
                        </option>
                      ))}
                    </select>
                  )}
                  {/* Department */}
                  {sortedDepartments.length > 0 && (
                    <select
                      value={studentDeptF}
                      onChange={e => setStudentDeptF(e.target.value)}
                      className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                    >
                      <option value="">All Departments</option>
                      {sortedDepartments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  )}
                  {/* Export */}
                  <button
                    onClick={() => {
                      const rows = filteredStudents.map(s => ({
                        'Name': s.fullName || '—',
                        'Email': s.email || '—',
                        'College': s.college?.collegeName || s.college?.institutionName || s.collegeName || collegeLabel,
                        'Department': typeof s.department === 'object'
                          ? (s.department?.abbreviation || s.department?.fullName || '—')
                          : (s.department || '—'),
                        'Status': s.status || '—',
                        'Completion %': s.completionRate != null ? `${Math.round(s.completionRate)}%` : 'N/A',
                        'Attendance': 'N/A',
                        'Placement Readiness': 'N/A',
                      }));
                      exportToCSV(rows, Object.keys(rows[0]).map(k => ({ header: k, accessor: k })), 'Students_Export');
                      toast.success('Students exported!');
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >
                    Export CSV
                  </button>
                  {(studentSearch || studentStatusF || studentDeptF || studentCollegeF) && (
                    <button
                      onClick={() => { setStudentSearch(''); setStudentStatusF(''); setStudentDeptF(''); setStudentCollegeF(''); }}
                      className={`text-xs font-bold px-3 py-2 rounded-xl ${isDark ? 'text-rose-400 hover:bg-rose-950/20' : 'text-rose-600 hover:bg-rose-50'}`}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Student table */}
              <SectionCard
                title="Student Directory"
                subtitle={`${filteredStudents.length} students${filters.college ? ` in ${collegeLabel}` : ' (all colleges)'}`}
                isDark={isDark}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-[#1a3884]/40' : 'border-gray-200'}`}>
                        {['Student', 'Email', 'College', 'Department', 'Status', 'Completion', 'Attendance', 'Placement'].map(h => (
                          <th key={h} className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-[#1a3884]/30' : 'divide-gray-100'}`}>
                      {filteredStudents.length === 0 ? (
                        <tr>
                          <td colSpan={8} className={`px-4 py-16 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p className="text-sm font-medium">No data available in this domain</p>
                          </td>
                        </tr>
                      ) : (
                        paginatedStudents.map((s, i) => {
                          const deptName = typeof s.department === 'object'
                            ? (s.department?.abbreviation || s.department?.fullName || s.department?.domain || '')
                            : (s.department || '');
                          const batchName = s.department?.batch || s.batch || '—';
                          return (
                            <tr key={s._id || i} className={`transition-colors ${isDark ? 'hover:bg-[#0F225C]/40' : 'hover:bg-gray-50'}`}>
                              <td className={`px-4 py-2.5 font-semibold text-xs ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{s.fullName || '—'}</td>
                              <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.email || '—'}</td>
                              <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{s.college?.collegeName || s.college?.institutionName || s.collegeName || collegeLabel}</td>
                              <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{deptName || '—'}</td>
                              <td className="px-4 py-2.5">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                  s.status === 'active' ? (isDark ? 'bg-emerald-950/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700') :
                                  s.status === 'pending' ? (isDark ? 'bg-amber-950/40 text-amber-400' : 'bg-amber-50 text-amber-700') :
                                  isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                                }`}>{s.status || 'pending'}</span>
                              </td>
                              <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                {s.completionRate != null ? `${Math.round(s.completionRate)}%` : '—'}
                              </td>
                              <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} title="No Attendance model">N/A</td>
                              <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} title="Requires per-student endpoint">N/A</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {totalStudentPages > 1 && (
                  <div className={`flex flex-col sm:flex-row items-center justify-between mt-4 pt-4 border-t gap-3 ${isDark ? 'border-[#1a3884]/30' : 'border-gray-200'}`}>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Showing {(studentPage - 1) * 10 + 1} to {Math.min(studentPage * 10, filteredStudents.length)} of {filteredStudents.length} students
                    </span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        onClick={() => setStudentPage(p => Math.max(1, p - 1))}
                        disabled={studentPage === 1}
                        className={`p-1.5 rounded-lg border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                      >
                        <ChevronLeft size={14} />
                      </button>
                      
                      {Array.from({ length: totalStudentPages }, (_, i) => i + 1).map(pageNum => (
                        <button
                          key={pageNum}
                          onClick={() => setStudentPage(pageNum)}
                          className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${
                            studentPage === pageNum
                              ? 'bg-[#1A3884] text-white'
                              : isDark
                                ? 'text-gray-400 hover:text-white hover:bg-[#1A3884]/20'
                                : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setStudentPage(p => Math.min(totalStudentPages, p + 1))}
                        disabled={studentPage === totalStudentPages}
                        className={`p-1.5 rounded-lg border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* Detailed progress link */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl p-6 border text-center ${isDark ? 'bg-[#002147] border-[#1a3884]/40' : 'bg-white border-gray-200 shadow-sm'}`}
              >
                <GraduationCap className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h3 className={`text-base font-bold mb-1.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>Detailed Student Progress</h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Individual timelines, course progress and assessment scores</p>
                <button
                  onClick={() => navigate('/college/student-progress')}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl text-sm font-bold shadow-md ${isDark ? 'bg-[#284BB3] hover:bg-[#1A3884]' : 'bg-[#1A3884] hover:bg-[#0F225C]'}`}
                >
                  <TrendingUp size={15} /> Open Student Progress
                </button>
              </motion.div>
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* LEADERBOARD TAB (unchanged)                                     */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === 'leaderboard' && (
            <motion.div key="leaderboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              <SectionCard title="Institute Rankings" subtitle="Comparative performance — sorted by total students" isDark={isDark}>
                <div className="space-y-3">
                  {colleges.length === 0 ? (
                    <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm font-medium">No institute data available</p>
                    </div>
                  ) : (
                    [...colleges]
                      .sort((a, b) => (b.totalStudents || 0) - (a.totalStudents || 0))
                      .slice(0, 20)
                      .map((college, i) => {
                        const medalBg = i === 0 ? '#1E3A8A' : i === 1 ? '#2563EB' : i === 2 ? '#3B82F6' : null;
                        return (
                          <motion.div
                            key={college._id}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                              isDark
                                ? `bg-[#0F225C]/40 border-[#1a3884]/40 ${i < 3 ? 'border-[#284BB3]/50' : ''}`
                                : `bg-gray-50 border-gray-200 ${i < 3 ? 'border-blue-200 bg-blue-50/40' : ''}`
                            }`}
                          >
                            <div
                              className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black ${
                                medalBg ? 'text-white shadow-md' : isDark ? 'bg-[#0F225C] text-gray-400' : 'bg-gray-200 text-gray-500'
                              }`}
                              style={medalBg ? { background: medalBg } : {}}
                            >
                              {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {college.collegeName || college.institutionName}
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {college.city || '—'} · {college.status || 'active'}
                              </p>
                            </div>
                            <div className="flex items-center gap-6 flex-shrink-0">
                              <div className="text-right">
                                <p className={`text-lg font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{college.totalStudents || 0}</p>
                                <p className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Students</p>
                              </div>
                              <div className="text-right hidden sm:block">
                                <p className={`text-lg font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{college.departments?.length || college.totalDepartments || 0}</p>
                                <p className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Depts</p>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })
                  )}
                </div>
              </SectionCard>
            </motion.div>
          )}

        </AnimatePresence>
      )}

      {/* Footer */}
      <p className={`text-center text-[10px] mt-8 pb-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
        SMAART Admin Operations Dashboard — data refreshed from platform APIs
      </p>
    </div>
  );
};

export default Analytics;
